'use strict';

/**
 * T67 W1 — Ed25519 identity-key derivation from master key
 *
 * Per PLAN-SEAMS-MULTIUSER-V1 LD-12 + W3 dependency:
 * Each user's master key deterministically produces an Ed25519 keypair used
 * for browser-side signing of JIT-grants (HARDEN-6). The private key seed is
 * derived via HKDF-SHA512 from the master key, bound to the identity purpose
 * label "oyster:identity:ed25519:v1".
 *
 * SECURITY properties:
 *   - Determinism: same masterKey → same keypair always
 *   - Domain separation: the HKDF label prevents the identity seed from
 *     colliding with any other subkey derived from the same master key
 *   - Memory-only: NEVER persist privateKey or seed; they live in JS memory
 *     for the duration of the signing operation, then should be GC'd
 *   - Public key persistence: publicKey MAY be stored in the user vault
 *     (it's public information and enables server-side signature verification)
 *   - No key extraction: privateKey is a raw Uint8Array seed — callers should
 *     sign through the provided signWithIdentity() rather than using it directly
 *
 * Implementation choices:
 *   - @noble/curves ed25519 (already in package.json v^2.0.1) — pure JS,
 *     battle-tested (audited by Cure53 + others), no native bindings
 *   - HKDF-SHA512 via @noble/hashes/hkdf (already in package.json v^2.0.1)
 *   - Private key = 32-byte random-looking seed derived via HKDF. @noble/curves
 *     ed25519.getPublicKey() and ed25519.sign() accept a 32-byte seed directly.
 *   - No WASM, no native modules — works in Node and browser bundles
 *
 * W3 integration point:
 *   Browser calls deriveIdentityKeypair(masterKey) → holds privateKey in memory
 *   → calls signWithIdentity(privateKey, grantPayload) → sends signature + publicKey
 *   to server → server calls verifyIdentity(publicKey, grantPayload, signature)
 *
 * KAT pin (computed during W1 — do not change without re-pinning):
 *   masterKey = 0x01 * 32
 *   publicKey = 8d0ff4bc5683af64b2139d114eecd009248004872eaab97e9580635b0ec4bc8e
 */

const { hkdf: nobleHkdf } = require('@noble/hashes/hkdf.js');
const { sha512 } = require('@noble/hashes/sha2.js');
const { ed25519 } = require('@noble/curves/ed25519.js');
const { ml_dsa65 } = require('@noble/post-quantum/ml-dsa.js');
const { verifyHybridOrClassical } = require('./hybrid-verify.js');

const TEXT_ENCODER = new TextEncoder();

// T323 P4-b (LD-7, LD-26) — long-lived identity bindings are dual-signed
// (Ed25519 + ML-DSA-65). The ML-DSA seed is HKDF-derived from the master key
// under a distinct label so it is independent of the Ed25519 seed. Grants stay
// Ed25519-only (LD-26) — they do NOT use these functions.
const IDENTITY_MLDSA_LABEL = 'oyster:identity:mldsa65:v1';

/**
 * HKDF label for identity key derivation.
 * Versioned so future rotations can use v2, v3, etc.
 */
const IDENTITY_LABEL = 'oyster:identity:ed25519:v1';

/**
 * KAT pin for test validation.
 * Computed from masterKey=0x01*32, HKDF label above, len=32.
 * publicKey = ed25519.getPublicKey(derivedSeed)
 */
const KAT_MASTER_KEY_01 = Buffer.alloc(32, 0x01);
const KAT_EXPECTED_PUBLIC_KEY_HEX = '8d0ff4bc5683af64b2139d114eecd009248004872eaab97e9580635b0ec4bc8e';

/**
 * Derive a deterministic Ed25519 keypair from a 32-byte master key.
 *
 * @param {Uint8Array|Buffer} masterKey — 32 bytes
 * @returns {{ privateKey: Uint8Array, publicKey: Uint8Array }}
 *   - privateKey: 32-byte seed (NEVER persist or log this)
 *   - publicKey:  32-byte Ed25519 public key (safe to persist in vault)
 */
function deriveIdentityKeypair(masterKey) {
  assertMasterKey(masterKey);
  const info = TEXT_ENCODER.encode(IDENTITY_LABEL);
  const seed = nobleHkdf(sha512, masterKey, new Uint8Array(0), info, 32);
  const publicKey = ed25519.getPublicKey(seed);
  return {
    privateKey: seed,   // 32-byte HKDF-derived seed
    publicKey,          // 32-byte Ed25519 public key
  };
}

/**
 * Sign a message with an Ed25519 private key (seed).
 *
 * @param {Uint8Array} privateKey — 32-byte seed from deriveIdentityKeypair
 * @param {Uint8Array|Buffer|string} message — bytes to sign
 * @returns {Uint8Array} 64-byte Ed25519 signature
 */
function signWithIdentity(privateKey, message) {
  if (!(privateKey instanceof Uint8Array) || privateKey.length !== 32) {
    throw new Error('Invalid privateKey: must be 32-byte Uint8Array from deriveIdentityKeypair');
  }
  const msgBytes = toMsgBytes(message);
  return ed25519.sign(msgBytes, privateKey);
}

/**
 * Verify an Ed25519 signature against a public key.
 *
 * @param {Uint8Array} publicKey — 32-byte Ed25519 public key
 * @param {Uint8Array|Buffer|string} message — bytes that were signed
 * @param {Uint8Array} signature — 64-byte Ed25519 signature
 * @returns {boolean} true if valid, false otherwise (non-throwing)
 */
function verifyIdentity(publicKey, message, signature) {
  if (!(publicKey instanceof Uint8Array) || publicKey.length !== 32) {
    return false;
  }
  if (!(signature instanceof Uint8Array) || signature.length !== 64) {
    return false;
  }
  try {
    const msgBytes = toMsgBytes(message);
    return ed25519.verify(signature, msgBytes, publicKey);
  } catch (_) {
    return false;
  }
}

// --- T323 P4-b: dual-sign (Ed25519 + ML-DSA-65) identity API ---

/**
 * Derive a deterministic DUAL identity keypair (Ed25519 + ML-DSA-65) from a
 * 32-byte master key. The Ed25519 half is identical to deriveIdentityKeypair()
 * (same label/KAT) so existing Ed25519-only verifiers keep working; the ML-DSA
 * half is derived under a distinct label.
 *
 * NOTE: ML-DSA-65 keygen consumes a 32-byte seed; @noble/post-quantum exposes
 * keygen(seed) for deterministic derivation.
 *
 * @param {Uint8Array|Buffer} masterKey — 32 bytes
 * @returns {{ privateKey: {ed25519: Uint8Array, mlDsa: Uint8Array},
 *            publicKey:  {ed25519: Uint8Array, mldsa65: Uint8Array} }}
 */
function deriveDualIdentityKeypair(masterKey) {
  assertMasterKey(masterKey);
  const edSeed = nobleHkdf(sha512, masterKey, new Uint8Array(0), TEXT_ENCODER.encode(IDENTITY_LABEL), 32);
  const edPub = ed25519.getPublicKey(edSeed);
  const dsaSeed = nobleHkdf(sha512, masterKey, new Uint8Array(0), TEXT_ENCODER.encode(IDENTITY_MLDSA_LABEL), 32);
  const dsaKp = ml_dsa65.keygen(dsaSeed);
  return {
    privateKey: { ed25519: edSeed, mlDsa: dsaKp.secretKey },
    publicKey: { ed25519: edPub, mldsa65: dsaKp.publicKey },
  };
}

/**
 * Dual-sign a message with a dual identity private key. Returns a structured
 * signature object { ed25519, mldsa65 } — NOT a concatenated buffer — so each
 * half can be verified independently by verifyHybridOrClassical (LD-20).
 *
 * @param {{ed25519: Uint8Array, mlDsa: Uint8Array}} dualPrivateKey
 * @param {Uint8Array|Buffer|string} message
 * @returns {{ ed25519: Uint8Array, mldsa65: Uint8Array }}
 */
function signDualIdentity(dualPrivateKey, message) {
  if (!dualPrivateKey || !(dualPrivateKey.ed25519 instanceof Uint8Array) || !(dualPrivateKey.mlDsa instanceof Uint8Array)) {
    throw new Error('signDualIdentity: privateKey must be { ed25519, mlDsa } from deriveDualIdentityKeypair');
  }
  const msgBytes = toMsgBytes(message);
  return {
    ed25519: ed25519.sign(msgBytes, dualPrivateKey.ed25519),
    mldsa65: ml_dsa65.sign(msgBytes, dualPrivateKey.mlDsa),
  };
}

/**
 * Verify a dual identity signature via the dual-accept shim. By default this is
 * OR-accept (either present half verifies) for the migration window. Pass
 * `{ requireDual: true }` for the strict post-migration mode (IH-a): both
 * halves must be PRESENT and both must verify — a stripped half is refused.
 * (`{ requireBoth: true }` only requires that all PRESENT halves verify; it
 * does NOT refuse an absent half — GOTCHA-896.)
 *
 * @param {{ed25519?: Uint8Array, mldsa65?: Uint8Array}} dualPublicKey
 * @param {Uint8Array|Buffer|string} message
 * @param {{ed25519?: Uint8Array, mldsa65?: Uint8Array}} sigObj
 * @param {{requireBoth?: boolean, requireDual?: boolean}} [opts]
 * @returns {boolean}
 */
function verifyDualIdentity(dualPublicKey, message, sigObj, opts) {
  return verifyHybridOrClassical(message, sigObj, dualPublicKey, opts);
}

/**
 * T323 P7(b) — explicit key-material zeroization helper. Callers that hold a
 * derived private seed / dual private key in a transient buffer should call this
 * the moment the signing operation completes, so the secret does not linger in
 * memory awaiting GC. Identity-key itself returns keys to the caller (it cannot
 * zeroize on the caller's behalf), so this is the supported zeroization seam.
 *
 * @param {...(Uint8Array|Buffer|{ed25519?:Uint8Array,mlDsa?:Uint8Array})} keys
 */
function zeroizeKey(...keys) {
  for (const k of keys) {
    if (k instanceof Uint8Array || Buffer.isBuffer(k)) {
      k.fill(0);
    } else if (k && typeof k === 'object') {
      for (const v of Object.values(k)) {
        if (v instanceof Uint8Array || Buffer.isBuffer(v)) v.fill(0);
      }
    }
  }
}

/**
 * T1449P P5 (LD-7b) — IS THIS KEYPAIR ACTUALLY USABLE FOR SIGNING?
 *
 * ⚠⚠ THE HAZARD THIS EXISTS FOR, found by execution: **a ZEROIZED private half
 * is still a structurally valid 32-byte Ed25519 seed.** `signWithIdentity`'s
 * length check passes, `ed25519.sign` signs happily, and the resulting
 * signature FAILS verification against the caller's published public key. So a
 * consumer that reads a keypair AFTER the registrar has called `zeroizeKey`
 * gets SILENT JUNK, not an error — the failure surfaces later, somewhere else,
 * as an unverifiable attestation nobody can explain.
 *
 * The general form of that defect is a keypair whose halves do not correspond.
 * Zeroization is just the common way to produce one. So this checks the general
 * property — derive the public key from the seed and compare — which catches
 * zeroization AND mismatched pairs AND a truncated seed with one test.
 *
 * ⚠ It lives HERE, in the module that owns the primitive, so every consumer
 * asks the same question. A per-consumer convention would be a deadline.
 *
 * @returns {{ok:true}|{ok:false, code:'ERR_IDENTITY_KEYPAIR_UNUSABLE', reason:string}}
 */
function checkIdentityKeypair(keypair) {
  const fail = (reason) => ({ ok: false, code: 'ERR_IDENTITY_KEYPAIR_UNUSABLE', reason });
  if (!keypair || typeof keypair !== 'object') return fail('no keypair supplied');
  const { privateKey, publicKey } = keypair;
  if (!(privateKey instanceof Uint8Array) || privateKey.length !== 32) {
    return fail('privateKey must be a 32-byte Uint8Array seed');
  }
  if (!(publicKey instanceof Uint8Array) || publicKey.length !== 32) {
    return fail('publicKey must be a 32-byte Uint8Array');
  }
  // The whole point: a zeroized seed passes every check above.
  let derived;
  try {
    derived = ed25519.getPublicKey(privateKey);
  } catch (_e) {
    return fail('privateKey is not a usable Ed25519 seed');
  }
  if (Buffer.compare(Buffer.from(derived), Buffer.from(publicKey)) !== 0) {
    return fail(
      'the private half does not correspond to the published public half — a signature made with '
      + 'it would be unverifiable. The usual cause is reading the keypair AFTER zeroizeKey().',
    );
  }
  return { ok: true };
}

// --- Internal helpers ---

function assertMasterKey(masterKey) {
  if (!(masterKey instanceof Uint8Array) && !Buffer.isBuffer(masterKey)) {
    throw new TypeError('Invalid masterKey: must be Uint8Array or Buffer');
  }
  if (masterKey.length !== 32) {
    throw new Error('Invalid masterKey: must be exactly 32 bytes (got ' + masterKey.length + ')');
  }
}

function toMsgBytes(message) {
  if (message instanceof Uint8Array) return message;
  if (Buffer.isBuffer(message)) return new Uint8Array(message.buffer, message.byteOffset, message.byteLength);
  if (typeof message === 'string') return TEXT_ENCODER.encode(message);
  throw new TypeError('Invalid message: must be string, Buffer, or Uint8Array');
}

module.exports = {
  deriveIdentityKeypair,
  // T1449P P5 (LD-7b) — a zeroized seed is still a structurally valid 32-byte
  // Ed25519 seed, so length checks cannot see it. See the docblock.
  checkIdentityKeypair,
  signWithIdentity,
  verifyIdentity,
  // T323 P4-b — dual-sign (Ed25519 + ML-DSA-65) for long-lived identity bindings.
  deriveDualIdentityKeypair,
  signDualIdentity,
  verifyDualIdentity,
  zeroizeKey,
  IDENTITY_LABEL,
  IDENTITY_MLDSA_LABEL,
  KAT_MASTER_KEY_01,
  KAT_EXPECTED_PUBLIC_KEY_HEX,
};
